import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getPortalTenantN8nExtras } from '../../../constants';
import {
  confirmPortalFrist,
  rejectPortalFrist,
  revokePortalFrist,
  selfAnswerPortalFrist,
} from './portalFristen';
import type {
  ApprovalFristPayload,
  FristApprovalCardState,
  FristApprovalDecision,
} from './fristApprovalTypes';

function normalizeNcPath(path?: string | null): string {
  return String(path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
    .toLowerCase();
}

function pathsMatch(a?: string | null, b?: string | null): boolean {
  const na = normalizeNcPath(a);
  const nb = normalizeNcPath(b);
  if (!na || !nb) return false;
  return na === nb || na.endsWith(nb) || nb.endsWith(na);
}

interface FristApprovalContextValue {
  customerId: string;
  customerEmail?: string;
  customerStatus?: string;
  cards: Record<string, FristApprovalCardState>;
  registerCard: (payload: ApprovalFristPayload) => void;
  resolveCard: (cardId: string, decision: FristApprovalDecision) => Promise<void>;
  revokeCard: (cardId: string) => Promise<void>;
  getCard: (cardId: string) => FristApprovalCardState | null;
  getCardForDocument: (args: {
    nextcloudPath?: string | null;
    nextcloudWebdavPath?: string | null;
    pbRecordId?: string | null;
  }) => FristApprovalCardState | null;
}

const FristApprovalContext = createContext<FristApprovalContextValue | null>(null);

export interface FristApprovalProviderProps {
  customerId: string;
  customerEmail?: string;
  customerStatus?: string;
  children: ReactNode;
}

export const FristApprovalProvider: React.FC<FristApprovalProviderProps> = ({
  customerId,
  customerEmail,
  customerStatus,
  children,
}) => {
  const [cards, setCards] = useState<Record<string, FristApprovalCardState>>({});

  const registerCard = useCallback((payload: ApprovalFristPayload) => {
    setCards((prev) => {
      const existing = prev[payload.card_id];
      if (existing) {
        const merged = { ...existing.payload, ...payload };
        if (
          existing.payload.ende_vorschau === merged.ende_vorschau &&
          existing.payload.kalender_trace === merged.kalender_trace &&
          existing.payload.motor_audit_id === merged.motor_audit_id &&
          existing.payload.regel_id === merged.regel_id
        ) {
          return prev;
        }
        return {
          ...prev,
          [payload.card_id]: {
            ...existing,
            payload: merged,
          },
        };
      }
      return {
        ...prev,
        [payload.card_id]: {
          payload,
          status: 'pending',
        },
      };
    });
  }, []);

  const resolveCard = useCallback(
    async (cardId: string, decision: FristApprovalDecision) => {
      let snapshot: FristApprovalCardState | undefined;
      setCards((prev) => {
        snapshot = prev[cardId];
        return prev;
      });
      if (!snapshot || snapshot.status !== 'pending') return;

      const tenantExtras = getPortalTenantN8nExtras(customerStatus, customerEmail);
      const baseBody = {
        customerId,
        card_id: cardId,
        approval_kind: snapshot.payload.approval_kind,
        frist_typ_vorschlag: snapshot.payload.frist_typ_vorschlag,
        start_datum: snapshot.payload.start_datum,
        regel_id: snapshot.payload.regel_id,
        motor_audit_id: snapshot.payload.motor_audit_id,
        akte_nc_path: snapshot.payload.akte_nc_path,
        quell_dokument_nc_path: snapshot.payload.quell_dokument_nc_path,
        quell_dokument_pb_id: snapshot.payload.quell_dokument_pb_id,
        bestaetigt_von: customerEmail?.trim() || undefined,
        ...tenantExtras,
        customerStatus: customerStatus?.trim() || undefined,
      };

      const api =
        decision === 'approved'
          ? confirmPortalFrist
          : decision === 'rejected'
            ? rejectPortalFrist
            : selfAnswerPortalFrist;

      const result = await api(baseBody);

      setCards((prev) => {
        const card = prev[cardId];
        if (!card) return prev;

        if (!result.ok) {
          return {
            ...prev,
            [cardId]: {
              ...card,
              status: 'error',
              errorMessage: result.error ?? 'Aktion fehlgeschlagen',
            },
          };
        }

        const status =
          decision === 'approved'
            ? 'approved'
            : decision === 'rejected'
              ? 'rejected'
              : 'self_answered';

        return {
          ...prev,
          [cardId]: {
            ...card,
            status,
            decision,
            resolvedAt: new Date().toISOString(),
            pbRecordId: result.pbRecordId ?? card.pbRecordId,
            payload: {
              ...card.payload,
              motor_audit_id: result.motor_audit_id ?? card.payload.motor_audit_id,
              ende_vorschau: result.ende_datum ?? card.payload.ende_vorschau,
            },
            errorMessage: undefined,
          },
        };
      });
    },
    [customerEmail, customerId, customerStatus],
  );

  const revokeCard = useCallback(
    async (cardId: string) => {
      let snapshot: FristApprovalCardState | undefined;
      setCards((prev) => {
        snapshot = prev[cardId];
        return prev;
      });
      if (!snapshot?.pbRecordId) return;

      const result = await revokePortalFrist({
        customerId,
        card_id: cardId,
        approval_kind: snapshot.payload.approval_kind,
        pb_record_id: snapshot.pbRecordId,
        bestaetigt_von: customerEmail?.trim() || undefined,
        ...getPortalTenantN8nExtras(customerStatus, customerEmail),
        customerStatus: customerStatus?.trim() || undefined,
      });

      if (!result.ok) {
        setCards((prev) => ({
          ...prev,
          [cardId]: {
            ...prev[cardId],
            status: 'error',
            errorMessage: result.error ?? 'Widerruf fehlgeschlagen',
          },
        }));
        return;
      }

      setCards((prev) => ({
        ...prev,
        [cardId]: {
          ...prev[cardId],
          status: 'revoked',
        },
      }));
    },
    [customerEmail, customerId, customerStatus],
  );

  const getCard = useCallback(
    (cardId: string) => cards[cardId] ?? null,
    [cards],
  );

  const getCardForDocument = useCallback(
    (args: {
      nextcloudPath?: string | null;
      nextcloudWebdavPath?: string | null;
      pbRecordId?: string | null;
    }) => {
      const values = Object.values(cards);
      const pendingOrApproved = values.filter(
        (c) => c.status === 'pending' || c.status === 'approved',
      );
      for (const card of pendingOrApproved) {
        const p = card.payload;
        if (args.pbRecordId && p.quell_dokument_pb_id === args.pbRecordId) return card;
        if (
          pathsMatch(p.quell_dokument_nc_path, args.nextcloudPath) ||
          pathsMatch(p.quell_dokument_nc_path, args.nextcloudWebdavPath)
        ) {
          return card;
        }
      }
      return null;
    },
    [cards],
  );

  const value = useMemo(
    () => ({
      customerId,
      customerEmail,
      customerStatus,
      cards,
      registerCard,
      resolveCard,
      revokeCard,
      getCard,
      getCardForDocument,
    }),
    [cards, customerEmail, customerId, customerStatus, registerCard, resolveCard, revokeCard, getCard, getCardForDocument],
  );

  return (
    <FristApprovalContext.Provider value={value}>{children}</FristApprovalContext.Provider>
  );
};

export function useFristApproval(): FristApprovalContextValue {
  const ctx = useContext(FristApprovalContext);
  if (!ctx) {
    throw new Error('useFristApproval muss innerhalb von FristApprovalProvider verwendet werden.');
  }
  return ctx;
}

export function useFristApprovalOptional(): FristApprovalContextValue | null {
  return useContext(FristApprovalContext);
}
